import React from 'react';
import PlainTextEditor from './PlainTextEditor';

class RichEditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('RichMentionEditor error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
      hasError: true
    });

    // Report error to monitoring service if available
    if (window.reportError) {
      window.reportError(error, {
        component: 'RichMentionEditor',
        errorInfo,
        retryCount: this.state.retryCount
      });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      const { 
        fallbackComponent: FallbackComponent,
        fallbackProps = {},
        showRetry = true,
        maxRetries = 2
      } = this.props;

      // If a custom fallback component is provided, use it
      if (FallbackComponent) {
        return (
          <FallbackComponent 
            {...fallbackProps}
            error={this.state.error}
            retry={this.handleRetry}
            canRetry={showRetry && this.state.retryCount < maxRetries}
          />
        );
      }

      // Default fallback to PlainTextEditor
      const {
        value,
        onChange,
        placeholder,
        maxLength,
        className,
        error,
        disabled,
        onBlur,
        onKeyUp,
        onKeyDown,
        autoFocus,
        rows
      } = this.props;

      return (
        <div className="space-y-2">
          {/* Show error message in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
              Rich editor failed, using fallback. Error: {this.state.error?.message}
              {showRetry && this.state.retryCount < maxRetries && (
                <button 
                  onClick={this.handleRetry}
                  className="ml-2 underline hover:no-underline"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          
          <PlainTextEditor
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            maxLength={maxLength}
            className={className}
            error={error}
            disabled={disabled}
            onBlur={onBlur}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
            rows={rows}
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default RichEditorErrorBoundary;